<template>
  <el-card class="table-card">
    <div class="table-title">月度达成<span>Mensal</span></div>
    <el-table
      :data="tableData"
      border
      fit
      highlight-current-row
      max-height="400"
      class="dashboard-table"
      style="width: 100%"
      row-key="_rowKey"
      :default-expand-all="true"
      :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
      :cell-class-name="cellClassName"
      :row-class-name="rowClassName"
      :header-row-class-name="headerRowClassName"
    >
      <el-table-column
        v-if="tableData.length > 0"
        label="年 Ano" width="180"
        align="center" fixed="left"
        label-class-name="fixed-header-label"
      >
        <template slot="header">
          <div class="fixed-header-label">
            <span class="fixed-header-label__zh">年</span>
            <span class="fixed-header-label__pt">Ano</span>
          </div>
        </template>
        <el-table-column
          label="月 Mês" width="180"
          align="center"
          label-class-name="fixed-header-label"
        >
          <template slot="header">
            <div class="fixed-header-label">
              <span class="fixed-header-label__zh">月</span>
              <span class="fixed-header-label__pt">Mês</span>
            </div>
          </template>
          <el-table-column
            label="大区 / 州 Região / UF" prop="region"
            align="left" width="180"
            label-class-name="fixed-header-label"
          >
            <template slot="header">
              <div class="fixed-header-label">
                <span class="fixed-header-label__zh">大区 / 州</span>
                <span class="fixed-header-label__pt">Região / UF</span>
              </div>
            </template>
          </el-table-column>
        </el-table-column>
      </el-table-column>
      <el-table-column
        v-for="year in columns"
        :key="year.key"
        :label="year.label"
        align="center"
      >
        <el-table-column
          v-for="month in year.months"
          :key="month.key"
          :label="month.label"
          align="center"
        >
          <el-table-column v-if="showQtd" label="QTD" align="center" min-width="90">
            <template slot-scope="{ row }">
              <span>{{ row[month.key + '_qtd'] }}</span>
            </template>
          </el-table-column>
          <el-table-column label="%" align="center" min-width="90">
            <template slot-scope="{ row }">
              <span :class="rateClass(row[month.key + '_rate'], row)">{{ row[month.key + '_rateFmt'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column
          :label="totalLabel" align="center"
          label-class-name="total-header-cell"
        >
          <template slot="header">
            <div class="total-header-label">
              <span class="total-header-label__zh">总计</span>
              <span class="total-header-label__pt">Total Mês</span>
            </div>
          </template>
          <el-table-column
            v-if="showQtd"
            label="QTD" align="center" min-width="90"
          >
            <template slot-scope="{ row }">
              <span>{{ year.total ? row[year.total.key + '_total_qtd'] : '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="%" align="center" min-width="90"
          >
            <template slot-scope="{ row }">
              <span :class="rateClass(year.total ? row[year.total.key + '_total_rate'] : null, row)">{{ year.total ? row[year.total.key + '_total_rateFmt'] : '-' }}</span>
            </template>
          </el-table-column>
        </el-table-column>
      </el-table-column>
    </el-table>
  </el-card>
</template>

<script>
import { buildTableRows, getMonthLabel, rateClass, TOTAL_LABEL } from './achievementUtils';

export default {
  name: 'MonthlyAchievement',
  props: {
    resData: {
      type: Object,
      default: () => ({})
    },
    // 是否显示 QTD 列
    showQtd: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      totalLabel: TOTAL_LABEL,
      columns: [],
      tableData: []
    };
  },
  watch: {
    resData: {
      immediate: true,
      handler(val) {
        this.dealData(val);
      }
    }
  },
  methods: {
    rateClass,
    dealData(res) {
      const resData = res && res.data ? res.data : {};
      const dateColList = resData.columns || [];
      const tableRowList = resData.rows || [];
      const isZh = this.$i18n.locale === 'zh';

      // 过滤列类型：MONTH 归入每月列，RANGE_TOTAL 归入"总计 Total Mês"列
      const monthColList = dateColList.filter(col => col.columnType === 'MONTH');
      const yearTotalColList = dateColList.filter(col => col.columnType === 'RANGE_TOTAL');

      // 构建表头日期列（按年份分组）
      const yearMap = {};
      monthColList.forEach(col => {
        const yearKey = col.key.substring(0, 4);
        const monthNum = parseInt(col.key.substring(5, 7), 10);
        if (!yearMap[yearKey]) {
          yearMap[yearKey] = { key: yearKey, label: yearKey, months: [] };
        }
        yearMap[yearKey].months.push({ key: col.key, label: getMonthLabel(monthNum), columnType: col.columnType });
      });
      yearTotalColList.forEach(col => {
        const firstYear = Object.values(yearMap)[0];
        if (firstYear) {
          firstYear.total = { key: col.key, label: this.totalLabel, columnType: col.columnType };
        }
      });
      this.columns = Object.values(yearMap);
      this.tableData = buildTableRows(tableRowList, dateColList, isZh);
      // 大区排序：按照总计列的准时签收率倒序排列
      this.sortRegionRowsByTotalRate();
    },
    // 大区排序：按照总计列（最后一列"总计 Total Mês"）的准时签收率倒序排列，Total 行保持置顶，无有效值的大区排在最后
    sortRegionRowsByTotalRate() {
      if (!this.tableData || this.tableData.length === 0) return;
      const totalCols = this.columns.filter(y => y.total).map(y => y.total);
      if (totalCols.length === 0) return;
      const sortKey = totalCols[totalCols.length - 1].key + '_total_rate';
      const getRate = (row) => {
        const v = row[sortKey];
        const n = Number(v);
        if (v === null || v === undefined || Number.isNaN(n)) return -Infinity;
        return n;
      };
      const totalRows = this.tableData.filter(row => row.rowType === 'TOTAL');
      const regionRows = this.tableData.filter(row => row.rowType !== 'TOTAL');
      regionRows.sort((a, b) => getRate(b) - getRate(a));
      this.tableData = totalRows.concat(regionRows);
    },
    cellClassName({ row, column }) {
      if (!row) return '';
      if (row.rowType === 'TOTAL' && column.label !== '%') {
        return 'total-cell';
      }
      // 仅大区名称所在单元格做特殊处理
      if (row.rowType === 'REGION' && column.property === 'region') {
        return 'region-name-cell';
      }
      return '';
    },
    rowClassName({ row }) {
      if (!row) return '';
      if (row.rowType === 'TOTAL') return 'total-row';
      if (row.rowType === 'REGION') return 'region-row';
      return '';
    },
    headerRowClassName({ rowIndex }) {
      return `header-row-${rowIndex}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import './achievementTable.scss';
</style>
